import { useState } from 'react'

import { useMutation, useQuery, useQueryClient } from 'react-query'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { VendorTypes } from '@webapp/modules/vendor/entities/enums/VendorTypeEnum'
import { type CreditCardProviderOption } from './CreditCardProviderOption'
import messages from './messages'
import { vendorRepository } from '../../data-access/repositories/VendorRepository'

// Component-local cached options query — ADR-3 select precedent
// (core/components/CurrencySelect/useCurrencySelect.ts).
const PROVIDER_OPTIONS_QUERY_KEY = ['selects', 'credit-card-providers']

export const useCreditCardProviderSelect = ({
  onCreated,
}: {
  onCreated: (provider: CreditCardProviderOption) => void
}) => {
  const intl = useProcurifyIntl()
  const queryClient = useQueryClient()

  const [isCreating, setIsCreating] = useState(false)
  const [newProviderName, setNewProviderName] = useState('')

  const { data, isFetching } = useQuery({
    queryKey: PROVIDER_OPTIONS_QUERY_KEY,
    queryFn: () => vendorRepository.listCreditCardProviders(),
    cacheTime: Infinity,
  })

  const { mutateAsync, isLoading: isSaving } = useMutation({
    mutationFn: (name: string) =>
      vendorRepository.createVendor({
        name,
        email: [],
        type: VendorTypes.CREDIT_CARD_PROVIDER,
      }),
  })

  // Inline provider creation (BHV-018): POST the new type-6 vendor, append
  // it to the cached options, and select it.
  const createProvider = async () => {
    const name = newProviderName.trim()
    if (!name) return

    try {
      const result = await mutateAsync(name)
      const provider: CreditCardProviderOption = {
        id: result.data.id,
        name: result.data.name,
      }

      queryClient.setQueryData<CreditCardProviderOption[]>(
        PROVIDER_OPTIONS_QUERY_KEY,
        (options) => [...(options ?? []), provider]
      )

      setIsCreating(false)
      setNewProviderName('')
      onCreated(provider)
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.createErrorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.createErrorToastMessage),
      })
    }
  }

  return {
    data: {
      providers: data ?? [],
    },
    states: {
      isFetching,
      isCreating,
      isSaving,
      newProviderName,
    },
    commands: {
      createProvider,
    },
    methods: {
      openCreate: () => setIsCreating(true),
      closeCreate: () => {
        setIsCreating(false)
        setNewProviderName('')
      },
      setNewProviderName,
    },
  }
}
